import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SessionTracker from "@/components/SessionTracker";
import { Search, Download, CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const fmtGs = (n) =>
  new Intl.NumberFormat("es-PY", { style: "currency", currency: "PYG", maximumFractionDigits: 0 }).format(n || 0);

/**
 * Semáforo de estados clínicos:
 *   verde     → tratamiento completado (8/8 sesiones)
 *   naranja   → en proceso (activo, sesiones < 8)
 *   rojo      → abandonaron (estado="abandonado" o pausado)
 */
function computeCategoria(p) {
  if (p.sesiones_completadas >= p.sesiones_totales || p.estado === "finalizado") return "completado";
  if (p.estado === "abandonado" || p.estado === "pausado") return "abandonado";
  return "en_proceso";
}

const CATEGORIAS = [
  {
    key: "en_proceso",
    label: "En proceso",
    accent: "orange",
    className: "border-orange-300 bg-orange-50/60",
    badgeClass: "bg-orange-100 text-orange-700 border-orange-200",
    dot: "bg-orange-500",
    description: "Pacientes activos que aún no completaron las 8 sesiones",
  },
  {
    key: "completado",
    label: "Completados con éxito",
    accent: "green",
    className: "border-emerald-300 bg-emerald-50/60",
    badgeClass: "bg-emerald-100 text-emerald-700 border-emerald-200",
    dot: "bg-emerald-500",
    description: "Pacientes que finalizaron las 8 sesiones del tratamiento",
  },
  {
    key: "abandonado",
    label: "Abandonaron",
    accent: "red",
    className: "border-red-300 bg-red-50/60",
    badgeClass: "bg-red-100 text-red-700 border-red-200",
    dot: "bg-red-500",
    description: "Pacientes que discontinuaron el tratamiento",
  },
];

export default function Historial() {
  const [pacientes, setPacientes] = useState([]);
  const [profesionales, setProfesionales] = useState([]);
  const [q, setQ] = useState("");
  const [activeTab, setActiveTab] = useState("en_proceso");

  const load = async () => {
    try {
      const [p, pr] = await Promise.all([api.get("/pacientes"), api.get("/profesionales")]);
      setPacientes(p.data);
      setProfesionales(pr.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const enriched = useMemo(
    () => pacientes.map((p) => ({ ...p, categoria: computeCategoria(p) })),
    [pacientes]
  );

  const filtered = enriched.filter((p) =>
    `${p.nombre} ${p.apellido} ${p.dni || ""}`.toLowerCase().includes(q.toLowerCase())
  );

  const grouped = {
    en_proceso: filtered.filter((p) => p.categoria === "en_proceso"),
    completado: filtered.filter((p) => p.categoria === "completado"),
    abandonado: filtered.filter((p) => p.categoria === "abandonado"),
  };

  const exportCSV = () => {
    const header = [
      "Categoría", "Apellido", "Nombre", "CI", "Teléfono", "Profesional",
      "Sesiones", "Costo total", "Pagado", "Saldo", "Estado",
    ];
    const rows = filtered.map((p) => {
      const prof = profesionales.find((x) => x.id === p.profesional_id);
      const catLabel = CATEGORIAS.find((c) => c.key === p.categoria)?.label || p.categoria;
      return [
        catLabel,
        p.apellido || "",
        p.nombre || "",
        p.dni || "",
        p.telefono || "",
        prof?.name || "",
        `${p.sesiones_completadas}/${p.sesiones_totales}`,
        p.costo_tratamiento || 0,
        p.total_pagado || 0,
        p.saldo_pendiente || 0,
        p.estado || "activo",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `historial-pacientes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cambiarEstado = async (id, nuevo) => {
    try {
      await api.patch(`/pacientes/${id}`, { estado: nuevo });
      toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="px-6 md:px-10 lg:px-14 py-10 md:py-14 pb-24 md:pb-14 max-w-[1500px]">
      <PageHeader
        eyebrow="Archivo clínico"
        title="Historial de pacientes"
        description="Registro clasificado por avance del tratamiento — 8 sesiones de duración."
        action={
          <button data-testid="export-csv-btn" onClick={exportCSV} className="btn-outline-pill">
            <Download className="w-4 h-4 mr-1.5" /> Exportar CSV
          </button>
        }
      />

      {/* Semáforo — resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        {CATEGORIAS.map((c) => (
          <button
            key={c.key}
            onClick={() => setActiveTab(c.key)}
            data-testid={`cat-${c.key}`}
            className={`text-left rounded-xl border-2 p-6 transition-all hover:shadow-md ${
              activeTab === c.key ? c.className + " shadow-md" : "border-neutral-200 bg-white hover:border-neutral-300"
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className={`w-3.5 h-3.5 rounded-full ${c.dot}`} />
              <span className="overline text-neutral-500">{c.label}</span>
            </div>
            <div className="text-4xl font-extrabold tracking-tight text-neutral-900 mb-1">
              {grouped[c.key].length}
            </div>
            <div className="text-xs text-neutral-500 leading-relaxed">{c.description}</div>
          </button>
        ))}
      </div>

      {/* Buscador */}
      <div className="card-clinic p-3 mb-6">
        <div className="flex items-center gap-2 px-2">
          <Search className="w-4 h-4 text-neutral-400" />
          <input
            data-testid="historial-search"
            className="w-full bg-transparent outline-none py-2 text-sm placeholder:text-neutral-400"
            placeholder="Buscar por nombre, apellido o CI"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-neutral-200 mb-6 overflow-x-auto">
        {CATEGORIAS.map((c) => (
          <button
            key={c.key}
            onClick={() => setActiveTab(c.key)}
            data-testid={`tab-${c.key}`}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === c.key
                ? "border-neutral-900 text-neutral-900"
                : "border-transparent text-neutral-500 hover:text-neutral-800"
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${c.dot}`} />
            {c.label}
            <span className="text-xs text-neutral-400">({grouped[c.key].length})</span>
          </button>
        ))}
      </div>

      {/* Lista */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {grouped[activeTab].length === 0 && (
          <div className="md:col-span-2 xl:col-span-3 card-clinic p-14 text-center text-sm text-neutral-500">
            No hay pacientes en esta categoría.
          </div>
        )}
        {grouped[activeTab].map((p) => {
          const prof = profesionales.find((x) => x.id === p.profesional_id);
          const cat = CATEGORIAS.find((c) => c.key === p.categoria);
          return (
            <div
              key={p.id}
              data-testid={`historial-card-${p.id}`}
              className={`rounded-xl border-2 p-5 bg-white transition-shadow hover:shadow-md ${
                cat.accent === "green"
                  ? "border-emerald-200"
                  : cat.accent === "orange"
                  ? "border-orange-200"
                  : "border-red-200"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="min-w-0">
                  <div className="font-bold text-neutral-900 tracking-tight text-lg truncate">
                    {p.nombre} {p.apellido}
                  </div>
                  <div className="text-xs text-neutral-500 truncate">
                    {p.dni ? `CI ${p.dni} · ` : ""}
                    {prof ? prof.name : "Sin profesional"}
                  </div>
                </div>
                <span
                  className={`text-xs font-medium px-2.5 py-1 rounded-full border ${cat.badgeClass} shrink-0`}
                >
                  {cat.label}
                </span>
              </div>

              <div className="mb-4">
                <div className="overline mb-1.5">Sesiones</div>
                <SessionTracker completed={p.sesiones_completadas} total={p.sesiones_totales} />
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs mb-4">
                <div>
                  <div className="overline mb-0.5">Total pagado</div>
                  <div className="font-semibold text-[hsl(var(--sage-hover))]">{fmtGs(p.total_pagado)}</div>
                </div>
                <div>
                  <div className="overline mb-0.5">Saldo</div>
                  <div className={`font-semibold ${p.saldo_pendiente > 0 ? "text-[hsl(var(--terracotta))]" : "text-neutral-600"}`}>
                    {fmtGs(p.saldo_pendiente)}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-neutral-100">
                <div className="flex gap-1">
                  {p.categoria !== "completado" && (
                    <button
                      onClick={() => cambiarEstado(p.id, "finalizado")}
                      data-testid={`marcar-completado-${p.id}`}
                      title="Marcar como completado"
                      className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 transition-colors"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                    </button>
                  )}
                  {p.categoria !== "en_proceso" && (
                    <button
                      onClick={() => cambiarEstado(p.id, "activo")}
                      data-testid={`marcar-activo-${p.id}`}
                      title="Reactivar tratamiento"
                      className="p-1.5 rounded-md text-orange-600 hover:bg-orange-50 transition-colors"
                    >
                      <Loader2 className="w-4 h-4" />
                    </button>
                  )}
                  {p.categoria !== "abandonado" && (
                    <button
                      onClick={() => cambiarEstado(p.id, "abandonado")}
                      data-testid={`marcar-abandonado-${p.id}`}
                      title="Marcar como abandonado"
                      className="p-1.5 rounded-md text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Link
                  to={`/pacientes/${p.id}`}
                  data-testid={`ver-ficha-${p.id}`}
                  className="text-sm font-medium text-[hsl(var(--sage-hover))] inline-flex items-center gap-1 hover:underline"
                >
                  Ver ficha <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
